import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { spinnerAPI } from '@/lib/api';
import { getDeviceFingerprint, hasSpunLocally, getStoredSpinResult, markSpinCompleted } from '@/lib/deviceFingerprint';
import SpinLeadForm from './SpinLeadForm';
import SpinResultModal from './SpinResultModal';
import './RewardSpinner.css';

/**
 * RewardSpinner - Main spin wheel component
 * 
 * Features:
 * - Fetches offers from backend
 * - Renders spinning wheel with 10 segments (each offer twice)
 * - Handles spin animation
 * - Shows lead form before spin
 * - Shows result with confetti after spin
 */
const RewardSpinner = ({ isOpen, onClose }) => {
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [alreadySpun, setAlreadySpun] = useState(false);
  const [existingResult, setExistingResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const wheelRef = useRef(null);

  // Fetch spinner config on mount
  useEffect(() => {
    if (isOpen) {
      fetchSpinnerConfig();
      checkEligibility();
    }
  }, [isOpen]);

  const fetchSpinnerConfig = async () => {
    try {
      setIsLoading(true);
      const response = await spinnerAPI.getConfig();
      const data = response.data?.data || response.data;
      setOffers(data.offers || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch spinner config:', err);
      setError('Failed to load spinner. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkEligibility = async () => {
    // First check localStorage
    if (hasSpunLocally()) {
      const storedResult = getStoredSpinResult();
      setAlreadySpun(true);
      setExistingResult(storedResult);
      return;
    }

    // Then check with backend
    try {
      const fingerprint = getDeviceFingerprint();
      const response = await spinnerAPI.checkEligibility(fingerprint);
      const data = response.data?.data || response.data;
      
      if (data.already_spun) {
        setAlreadySpun(true);
        setExistingResult({
          couponCode: data.coupon_code,
          offerName: data.offer_name,
          planCategory: data.plan_category,
          planCategoryDisplay: data.plan_category_display,
          planId: data.plan_id,
        });
        // Also store locally for future checks
        markSpinCompleted(data.coupon_code, data.offer_name, data.plan_category, data.plan_category_display, data.plan_id);
      }
    } catch (err) {
      console.error('Failed to check eligibility:', err);
    }
  };

  const handleSpinClick = () => {
    if (alreadySpun) {
      // Show existing result with full info including plan category
      setSpinResult({
        coupon_code: existingResult.couponCode,
        offer_name: existingResult.offerName,
        plan_category: existingResult.planCategory,
        plan_category_display: existingResult.planCategoryDisplay,
        plan_id: existingResult.planId,
      });
      setShowResult(true);
      return;
    }
    setShowLeadForm(true);
  };

  const handleLeadSubmit = async (leadData) => {
    try {
      const fingerprint = getDeviceFingerprint();
      const response = await spinnerAPI.claim({
        device_fingerprint: fingerprint,
        ...leadData,
      });
      
      const result = response.data?.data || response.data;
      
      // Store result locally (include plan_category and plan_id for future use)
      markSpinCompleted(result.coupon_code, result.offer_name, result.plan_category, result.plan_category_display, result.plan_id);
      
      // Close lead form
      setShowLeadForm(false);
      
      // Start spinning animation
      startSpinAnimation(result);
      
      return { success: true };
    } catch (err) {
      console.error('Failed to claim spin:', err);
      const errorData = err.response?.data;
      const statusCode = err.response?.status;
      
      if (errorData?.code === 'ALREADY_SPUN') {
        setAlreadySpun(true);
        setExistingResult({
          couponCode: errorData.errors?.coupon_code,
          offerName: errorData.errors?.offer_name,
        });
        setShowLeadForm(false);
        return { success: false, error: 'You have already claimed your reward!' };
      }
      
      // Handle duplicate phone number error
      if (errorData?.code === 'DUPLICATE_PHONE') {
        return { 
          success: false, 
          error: 'This phone number has already been used to claim a reward. Please use a different phone number.' 
        };
      }
      
      // Handle service unavailable (503) - offer/coupon not configured
      if (statusCode === 503) {
        return { 
          success: false, 
          error: 'This offer is temporarily unavailable. Please try again later or contact support.' 
        };
      }
      
      // Handle validation errors
      if (statusCode === 400 && errorData?.errors) {
        const firstError = Object.values(errorData.errors)[0];
        if (Array.isArray(firstError)) {
          return { success: false, error: firstError[0] };
        }
        return { success: false, error: firstError || errorData?.message || 'Invalid input. Please check your details.' };
      }
      
      return { 
        success: false, 
        error: errorData?.message || 'Something went wrong. Please try again.' 
      };
    }
  };

  const startSpinAnimation = (result) => {
    setIsSpinning(true);
    
    // Calculate rotation to land on winning segment
    const segmentIndex = (result.segment - 1) * 2; // Each offer appears twice
    const segmentAngle = 360 / 10; // 10 segments
    const targetAngle = segmentAngle * segmentIndex + segmentAngle / 2;
    
    // Add multiple full rotations for effect
    const totalRotation = 360 * 5 + (360 - targetAngle);
    
    setRotation(totalRotation);
    
    // Show result after animation
    setTimeout(() => {
      setIsSpinning(false);
      setSpinResult(result);
      setShowResult(true);
      setAlreadySpun(true);
    }, 5000); // 5 second animation
  };

  const handleClose = () => {
    setShowLeadForm(false);
    setShowResult(false);
    onClose();
  };

  if (!isOpen) return null;

  // Double the offers to get 10 segments
  const segments = [...offers, ...offers];

  return (
    <div className="reward-spinner-overlay">
      <div className="reward-spinner-modal">
        {/* Close button */}
        <button className="spinner-close-btn" onClick={handleClose}>
          <X size={24} />
        </button>

        {/* Header */}
        <div className="spinner-header">
          <h2>🎉 Spin & Win! 🎉</h2>
          <p>Try your luck and win exclusive discounts!</p>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="spinner-loading">
            <div className="spinner-loader"></div>
            <p>Loading...</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="spinner-error">
            <p>{error}</p>
            <button onClick={fetchSpinnerConfig}>Try Again</button>
          </div>
        )}

        {/* No offers configured */}
        {!isLoading && !error && offers.length === 0 && (
          <div className="spinner-unavailable">
            <p>🎁 Spinner coming soon! Check back later.</p>
          </div>
        )}

        {/* Wheel */}
        {!isLoading && !error && offers.length > 0 && (
          <div className="spinner-wheel-container">
            {/* Pointer */}
            <div className="spinner-pointer">▼</div>
            
            {/* Wheel */}
            <div 
              ref={wheelRef}
              className={`spinner-wheel ${isSpinning ? 'spinning' : ''}`}
              style={{ 
                transform: `rotate(${rotation}deg)`,
                transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
              }}
            >
              {segments.map((offer, index) => (
                <div
                  key={index}
                  className="spinner-segment"
                  style={{
                    '--segment-color': offer.color,
                    '--segment-rotation': `${(360 / 10) * index}deg`,
                  }}
                >
                  <span className="segment-text">{offer.name}</span>
                </div>
              ))}
            </div>

            {/* Spin button */}
            <button 
              className={`spin-button ${isSpinning ? 'disabled' : ''} ${alreadySpun ? 'claimed' : ''}`}
              onClick={handleSpinClick}
              disabled={isSpinning}
            >
              {isSpinning ? '🎰' : alreadySpun ? 'View Prize' : 'SPIN!'}
            </button>
          </div>
        )}

        {/* Already spun message */}
        {alreadySpun && !showResult && !showLeadForm && (
          <div className="already-spun-message">
            <p>You've already claimed your reward! Click the button to view your prize.</p>
          </div>
        )}
      </div>

      {/* Lead capture form */}
      {showLeadForm && (
        <SpinLeadForm 
          onSubmit={handleLeadSubmit}
          onClose={() => setShowLeadForm(false)}
        />
      )}

      {/* Result modal with confetti */}
      {showResult && spinResult && (
        <SpinResultModal 
          result={spinResult}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default RewardSpinner;
